import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, Eye, Palette, Layout, Monitor, Camera, MessageCircle } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";

const Highlight = ({ children }) => (
  <span className="relative inline-block font-accent">
    <span className="absolute left-0 right-0 bottom-[0.08em] h-[0.8em] bg-[#BE123C] -z-0" />
    <span className="relative z-10">{children}</span>
  </span>
);

const OrganicImage = ({ src, alt, className = "" }) => (
  <div className={`aspect-[4/5] overflow-hidden max-w-[65%] lg:max-w-none ${className}`} style={{ WebkitMaskImage: 'url(/images/mood/shape-mask.png)', maskImage: 'url(/images/mood/shape-mask.png)', WebkitMaskSize: '100% 100%', maskSize: '100% 100%', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat' }}>
    <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
  </div>
);

export default function VisualIdentityPagePL() {
  return (
    <main data-testid="visual-identity-page-pl">

      {/* 1. HERO */}
      <section className="pt-32 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/flower-hand.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/80 lg:bg-transparent" />
          <div className="hidden lg:block absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(28,18,16,0.88) 40%, rgba(28,18,16,0.5) 65%, rgba(28,18,16,0.3) 100%)' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">PEŁNA REALIZACJA</span>
            <h1 className="mb-6">
              <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">FULL BRAND SYSTEM</span>
              <span className="font-accent text-2xl md:text-3xl lg:text-4xl text-[#BE123C] block leading-[1.1] mt-2">Przestań tłumaczyć swoją wartość. Zacznij ją pokazywać.</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-xl mb-10">
              Twój biznes jest dobry. Twoi klienci o tym wiedzą. Problem w tym, że wszyscy inni muszą się tego domyślać. Ten program sprawia, że Twoja marka mówi za Ciebie, więc możesz przestać się powtarzać. Wszystko z <Link to="/brand-foundation" className="text-white/80 underline hover:text-white">Brand Foundation</Link> plus kompletna identyfikacja wizualna zbudowana na lata.
            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              {["Pełne Brand Foundation", "Kompletny system wizualny", "Szablony i materiały", "Miesiąc wsparcia po wdrożeniu"].map((label, i) => (
                <div key={i} className="flex items-center gap-2.5 bg-white/5 border border-white/10 px-4 py-2">
                  <Check size={12} className="text-[#D4A0A0]" />
                  <span className="font-body text-xs text-white/60 tracking-wide">{label}</span>
                </div>
              ))}
            </div>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
              UMÓW ROZMOWĘ <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. PROBLEM */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">PRAWDZIWY PROBLEM</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">TWÓJ BIZNES JEST DOBRY</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">ale Twoja marka tego nie pokazuje</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-lg">
                <p>Znasz to uczucie. Ktoś prosi Cię o link do strony i coś w Tobie się kurczy. Strona wygląda ok, ale nie niesie Twojego poziomu. Instagram wygląda jak moodboard z pięciu różnych projektów. Poprawiasz, przeprojektowujesz, przepisujesz i nic się nie trzyma.</p>
                <p>Problem nie polega na tym, że potrzebujesz lepszego logo. Twoje wizualia, Twój przekaz i Twoje pozycjonowanie nie są połączone z tą samą prawdą. Ludzie wahają się, bo czują, że coś jest nie tak. To naprawiamy.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center space-y-10">
              {[
                { title: "Ludzie wchodzą na Twoją stronę i wychodzą", body: "Wizualnie jest ok, ale nic nie sygnalizuje, dlaczego powinni zostać albo Ci zaufać." },
                { title: "Twój feed wygląda jak pięć różnych biznesów", body: "Każdy post ma inną czcionkę, filtr albo energię. Nie ma systemu, który to trzyma razem." },
                { title: "Ciągle przeprojektowujesz zamiast sprzedawać", body: "Co kilka miesięcy zmieniasz logo, kolory, układ. Marka nie może się ustabilizować, bo brakuje fundamentu pod spodem." },
              ].map((card, i) => (
                <div key={i} className="border-l-2 border-[#BE123C] pl-6">
                  <h3 className="font-body text-base font-semibold text-[#1C1210] mb-1.5">{card.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{card.body}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. CO OBEJMUJE */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">CO OBEJMUJE</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">DWIE WARSTWY PRACY</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">strategia, potem ekspresja wizualna</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="bg-white border border-[#e5e2de] p-8 md:p-10">
              <span className="font-eyebrow text-[10px] tracking-[2px] text-[#8B1A3A] mb-4 block">WARSTWA PIERWSZA</span>
              <h3 className="font-display text-2xl text-[#1C1210] mb-2">Brand Foundation</h3>
              <p className="font-accent text-base text-[#BE123C] mb-4">Kim jesteś i za czym stoi Twoja marka</p>
              <p className="font-body text-sm text-[#9A9590] mb-6">Wszystko zawarte w <Link to="/brand-foundation" className="text-[#BE123C] underline hover:text-[#9F1239]">Brand Foundation</Link></p>
              <ul className="space-y-3">
                {[
                  "Audyt obecnej prezencji marki",
                  "Wartości, wizja i pozycjonowanie",
                  "Definicja idealnego klienta",
                  "Wywiad tożsamości marki",
                  "Messaging i ton głosu",
                  "USP i przewaga konkurencyjna",
                  "Brand Foundation Book",
                  "DNA Voice Agent (Twój agent AI piszący Twoim głosem)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={14} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="bg-white border border-[#e5e2de] p-8 md:p-10">
              <span className="font-eyebrow text-[10px] tracking-[2px] text-[#8B1A3A] mb-4 block">WARSTWA DRUGA</span>
              <h3 className="font-display text-2xl text-[#1C1210] mb-2">System identyfikacji wizualnej</h3>
              <p className="font-accent text-base text-[#BE123C] mb-6">Jak Twoja marka wygląda i co czujesz wszędzie</p>
              <ul className="space-y-3">
                {[
                  "Trzy kierunki logo (wybierasz jeden)",
                  "Pełny system projektowy: typografia, kolory, wzory, logika layoutu",
                  "Logo i sygnet marki",
                  "Szablony social media (posty, stories, highlights)",
                  "Dokumenty biznesowe (faktury, oferty, prezentacje)",
                  "Dokument wytycznych marki",
                  "Szablony Canva lub Figma dla Twojego zespołu",
                  "Biblioteka zdjęć (30 zdjęć brandowych + 30 stockowych)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={14} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCES */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">PROCES</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">OD PIERWSZEJ ROZMOWY</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">do marki, która działa sama</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery i fundament", body: "Zaczynamy od pracy Brand Foundation: wywiady, warsztaty, pozycjonowanie. Wychodzisz z Brand Foundation Book i DNA Voice Agent.", weeks: "Tygodnie 1-3" },
              { step: "02", title: "Trzy kierunki wizualne", body: "Tworzę trzy odrębne koncepcje kreatywne dla Twojej identyfikacji wizualnej. Każda osadzona w pracy strategicznej. Wybierasz tę, która czujesz.", weeks: "Tydzień 4" },
              { step: "03", title: "Budowa systemu", body: "Rozwijam wybrany kierunek w pełny system: logo, typografia, kolory, logika layoutu, szablony, kit social media, dokumenty biznesowe i wytyczne.", weeks: "Tygodnie 5-7" },
              { step: "04", title: "Wsparcie wdrożeniowe", body: "Po dostawie jestem dostępna przez miesiąc na WhatsApp, od poniedziałku do piątku w godzinach pracy. Pomagam Ci wdrożyć nową tożsamość i odpowiadam na pytania.", weeks: "Miesiąc 3" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="border-t border-[#1C1210]/15 pt-6">
                <span className="font-display text-4xl text-[#BE123C] block mb-4 leading-none">{s.step}</span>
                <h3 className="font-serif text-lg text-[#1C1210] mb-2">{s.title}</h3>
                <p className="font-body text-sm text-[#6B6560] leading-[1.7] mb-4">{s.body}</p>
                <span className="font-body text-xs text-[#9A9590] uppercase tracking-[1px]">{s.weeks}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. TESTIMONIAL */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">OPINIE</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">CO MÓWIĄ ZAŁOŻYCIELE</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">po wdrożeniu pełnego systemu</span>
            </h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="bg-white p-8 md:p-12 relative border border-[#e5e2de]">
              <span className="absolute left-6 top-4 text-7xl font-serif text-[#BE123C]/10 select-none pointer-events-none leading-none">&ldquo;</span>
              <div className="relative space-y-4 font-body text-base md:text-lg text-[#4A4540] leading-relaxed max-w-3xl">
                <p>Wcześniej potrafiłam siedzieć godzinami nad grafiką i efekt i tak nie był taki, jak chciałam. Był nawet moment, że wydawało mi się, że już jest dobrze, a potem zobaczyłam materiały przygotowane przez Olę i zobaczyłam, jaka jest różnica.</p>
                <p>Teraz mam gotowe szablony. Podmieniam zdjęcie i tekst i wszystko jest spójne, estetyczne i wygląda profesjonalnie. To oszczędza mi bardzo dużo czasu i daje pewność, że to po prostu dobrze wygląda.</p>
                <p>Nauczyłam się tego, czego wcześniej nie wiedziałam, czyli jak pisać treści, w jakim stylu je tworzyć i co właściwie chcemy komunikować klientowi. W końcu wiem, czym jest marka Galikowski Finanse i jaka ma być dla klienta.</p>
              </div>
              <p className="font-body text-sm text-[#9A9590] mt-8 tracking-wider uppercase">Galikowski Finanse, Full Brand System</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. DIVIDER */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              Kiedy Twoja marka jest niespójna, ludzie się wahają. Nie zawsze potrafią nazwać dlaczego. Po prostu czują: coś tu nie gra. Ten program usuwa to uczucie, łącząc Twoją prawdę z Twoimi wizualiami i Twoim przekazem. Dlatego zaufanie buduje się <Highlight>szybciej.</Highlight> Dlatego kupowanie staje się <Highlight>łatwiejsze.</Highlight>
            </p>
          </motion.div>
        </div>
      </section>


      {/* 7. CO SIĘ ZMIENIA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">PO ZAKOŃCZENIU PRACY</span>
            <h2 className="mb-12">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">CO SIĘ FAKTYCZNIE ZMIENIA</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">w Twojej codzienności</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { Icon: Eye, text: "Wysyłasz swój link bez ściskania w żołądku. Twoja strona, Instagram i oferta niosą Twój poziom. Przestajesz się wahać przed dzieleniem się linkiem, bo w końcu wygląda i brzmi jak Ty." },
              { Icon: Palette, text: "Przestajesz siedzieć godzinami nad jedną grafiką. Z gotowymi szablonami podmieniasz zdjęcie i linijkę tekstu i wszystko wychodzi spójne i profesjonalne. Ten czas wraca do Twoich klientów." },
              { Icon: Monitor, text: "Ludzie ufają Ci szybciej. Obcy wchodzi na Twoją stronę i od razu łapie co robisz, dla kogo to jest i jakiego doświadczenia może się spodziewać. Koniec z tłumaczeniem się przy każdej rozmowie." },
              { Icon: Layout, text: "Wiesz co powiedzieć i jak to powiedzieć. Wcześniej pisałaś treści na wyczucie. Teraz masz jasny kierunek dla swojego przekazu, tonu i tego co chcesz komunikować. Zgadywanie się kończy." },
              { Icon: Camera, text: "Widzisz różnicę między tym co miałaś wcześniej a tym co masz teraz. Jedna z moich klientek powiedziała mi, że myślała, że jej stara marka jest ok, dopóki nie zobaczyła nowych materiałów i zrozumiała, jak duża była ta luka." },
              { Icon: MessageCircle, text: "Rozumiesz swoją własną markę po raz pierwszy. Potrafisz wyjaśnić kim jesteś i dlaczego to ma znaczenie bez jąkania się. Rozmowy o cenach stają się łatwiejsze, bo postrzegana wartość w końcu zgadza się z prawdziwą." },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex items-start gap-4 bg-[#f5f5f7] p-6 border border-[#e5e2de]">
                <item.Icon size={20} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                <p className="font-body text-sm text-[#4A4540] leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. DLA KOGO */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">DLA KOGO TO JEST</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">DLA OSÓB, KTÓRE MAJĄ DOŚĆ</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">brandingu "prawie dobrze"</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>Masz realne wyniki, realnych klientów i realną głębię. Twój biznes nie jest problemem. Sposób, w jaki się prezentuje, tak. Próbowałaś Canvy, próbowałaś freelancerów, może próbowałaś robić to sama. Za każdym razem efekt wyglądał ok, no ale nie czuł się jak Ty.</p>
                <p>Ten program jest na moment, kiedy jesteś gotowa przestać łatać i zacząć budować markę, która trzyma się wszędzie: na stronie, na Instagramie, na wizytówce, w prezentacji, w DM-ie.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center">
              <div className="space-y-2 mb-8">
                <p className="font-body text-sm text-[#4A4540] leading-relaxed">
                  <span className="font-semibold text-[#1C1210]">Zacznij tutaj, jeśli</span> chcesz system marki, który działa w każdym punkcie kontaktu, zbudowany na prawdziwym fundamencie strategicznym, z identyfikacją wizualną zaprojektowaną na lata.
                </p>
                <p className="font-body text-sm text-[#9A9590] leading-relaxed italic">
                  To nie jest dla Ciebie, jeśli szukasz szybkiego odświeżenia logo albo paczki szablonów social media bez pracy strategicznej. Jeśli potrzebujesz samej strategii, zobacz <Link to="/pl/brand-foundation" className="text-[#BE123C] underline hover:text-[#9F1239]">Brand Foundation</Link>.
                </p>
              </div>
              <div className="space-y-4">
                {[
                  "Osoby prowadzące własne biznesy i małe zespoły (do ~50 osób)",
                  "Biznesy z realną trakcją, których marka nie nadążyła",
                  "Każdy kto przechodzi pivot, rebranding albo moment znaczącego wzrostu",
                  "Osoby, które chcą osobistej, praktycznej pracy z dyrektorem kreatywnym",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={14} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 9. KTO ZA TYM STOI */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <OrganicImage src="/images/mood/flower-hand.jpg" alt="KALZA Studio" className="shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">KTO ZA TYM STOI</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">ALEKSANDRA KAŁUŻA</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">KALZA Studio</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#4A4540] leading-[1.7]">
                <p>Lata spędziłam wewnątrz globalnych zespołów brandowych, prowadząc kierunek kreatywny w Just Eat Takeaway i realizując kampanie dla Heineken, Samsung i UEFA Champions League. Moim zadaniem było pilnować, żeby każdy punkt kontaktu niósł tę samą prawdę. Dalej to robię, tylko teraz dla osób budujących własne biznesy.</p>
                <p>Wchodzę do środka biznesu, audituję rzeczywistość wewnętrzną i zewnętrzną i prowadzę decyzje przez kontrolowany proces. Efekt to nie paczka logo, która umiera w folderze. To system, na którym jedzie biznes: szablony, materiały, wytyczne i logika designu, która zapobiega chaosowi.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. INWESTYCJA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">INWESTYCJA</span>
            <h2 className="mb-8">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">CENA DOPASOWANA</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">do tego, gdzie jest Twój biznes</span>
            </h2>
            <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-2xl mb-12">
              <p>Full Brand System to inwestycja pięciocyfrowa. Dokładna kwota zależy od skali Twojego biznesu, bo soloprzedsiębiorca i firma z pełnym zespołem wymagają innej głębokości pracy. Ustalamy cenę po rozmowie o Twojej sytuacji, tak żeby odzwierciedlała to, czego Twój biznes naprawdę potrzebuje.</p>
              <p>Nowa tożsamość to duża decyzja i nie powinna być podejmowana w pośpiechu. Harmonogram ok. dwóch miesięcy jest celowy: obejmuje czas na research, tworzenie, refleksję, poprawki i planowanie wdrożenia.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {[
                { label: "Solo", desc: "Jedna osoba, jasna oferta, gotowa na pełny system marki, który się trzyma." },
                { label: "Mały zespół", desc: "Kilka osób, różne oferty, marka musi działać spójnie w całym biznesie." },
                { label: "Rosnąca firma", desc: "Większy zespół, złożona struktura, spójność marki między działami i punktami kontaktu." },
              ].map((tier, i) => (
                <div key={i} className="bg-[#f5f5f7] p-6 border border-[#e5e2de]">
                  <h4 className="font-serif text-base text-[#1C1210] mb-2">{tier.label}</h4>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{tier.desc}</p>
                </div>
              ))}
            </div>
            <p className="font-body text-sm text-[#9A9590] mb-10">Każda współpraca obejmuje pełne Brand Foundation, kompletny system identyfikacji wizualnej, szablony, DNA Voice Agent i miesiąc wsparcia wdrożeniowego przez WhatsApp.</p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
              POROZMAWIAJMY O INWESTYCJI <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* 8. FINAL CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">KIEDY BĘDZIESZ GOTOWA</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">ZBUDUJ MARKĘ, KTÓRA MÓWI</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-2">żebyś mogła przestać tłumaczyć</span>
            </h2>
            <p className="font-body text-base text-white/70 leading-relaxed mb-10">
              Jeśli Twój biznes zasługuje na więcej niż to, co pokazuje Twoja marka, to jest praca, która zamyka tę lukę. Umów rozmowę, a powiem Ci dokładnie, co widzę.
            </p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
              UMÓW ROZMOWĘ <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
